/**
 * RadioQuestion component
 *
 * Description:
 * Renders a multiple-choice question with radio buttons, allowing users to select one answer and submit it.
 * Handles answer selection and dispatches updates to the Redux store.
 *
 * Features:
 * - Displays all choices for a question as radio buttons.
 * - Handles user selection and updates answers in Redux.
 * - Uses Material-UI for UI elements.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';

import { getQuiz, saveUpdateAnswer } from 'store/lesson-actions';

import classes from './RadioQuestion.module.css';

const RadioQuestion = props => {
  const { t } = useTranslation(['lesson']);
  const dispatch = useDispatch();
  const dataAcademy = useSelector(state => state.academyInfo);
  const dataLesson = useSelector(state => state.lessonInfo);
  // Local state for selected answer
  const [answer, setAnswer] = useState('');

  useEffect(() => {
    props.questionData.choices.map(choice => {
      if (choice.answer) {
        setAnswer(choice.idChoice.toString());
        props.nextQuestion();
      }
    });
  }, []);

  /**
   * Handles radio button change event for answer selection.
   * Updates Redux and triggers next question.
   */
  const handleChange = event => {
    setAnswer(event.target.value);
    props.nextQuestion();
    (async () => {
      const answer = await saveUpdateAnswer(
        props.idApplication,
        event.target.value,
        true
      );
      if (
        props.currentQuestion !==
        dataLesson.quizData.questions.length
      ) {
        if (answer) {
          dispatch(
            getQuiz(
              dataLesson.data.idQuiz,
              dataLesson.quizEvaluableData.idApplication,
              dataAcademy.data.shortName
            )
          );
        }
      }
    })();
  };
  return (
    <>
      {/* Custom prop: 'className' for question styling */}
      <p className={classes.question}>{props.questionData.body}</p>
      <p className={classes.text}>{t('select_an_option')}</p>
      <FormControl>
        {/* Custom prop: 'value' for selected answer, 'onChange' for answer selection */}
        <RadioGroup value={answer} onChange={handleChange}>
          {props.questionData.choices.map(choice => (
            <FormControlLabel
              key={choice.idChoice}
              value={choice.idChoice}
              // Custom prop: 'control' passes Radio with custom styles
              control={
                <Radio
                  sx={{
                    color: '#a41034',
                    '&.Mui-checked': {
                      color: '#eeeeee'
                    }
                  }}
                />
              }
              label={choice.body}
              // Custom prop: 'className' for answer styling
              className={classes.answer}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </>
  );
};

RadioQuestion.propTypes = {
  /**
   * Question data object containing choices and question info
   */
  questionData: PropTypes.object,
  /**
   * Application ID for answer submission
   */
  idApplication: PropTypes.number,
  /**
   * Current question index
   */
  currentQuestion: PropTypes.number,
  /**
   * Function to go to the next question
   */
  nextQuestion: PropTypes.func
};

RadioQuestion.defaultProps = {
  questionData: {},
  idApplication: undefined,
  currentQuestion: undefined,
  nextQuestion: undefined
};

export default RadioQuestion;
